import { Play, Pause, RotateCcw, SkipBack, SkipForward, Maximize2, Minimize2, Route } from "lucide-react";
import { phasesSorted, phaseAt } from "@/lib/playEngine";

const SPEEDS = [0.25, 0.5, 1];

export function PlaybackBar({ play, pb, trails, setTrails, full, setFull, compact }) {
  const phases = phasesSorted(play);
  const cur = phaseAt(play, pb.t);
  const prev = () => { const p = [...phases].reverse().find((ph) => ph.time < pb.t - 0.05); pb.seek(p ? p.time : 0); };
  const next = () => { const p = phases.find((ph) => ph.time > pb.t + 0.05); if (p) pb.playRange(pb.t, p.time); else pb.seek(pb.duration); };

  return (
    <div className="space-y-2" data-testid="playback-bar">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-display text-lg uppercase tracking-wide text-[#FF8A1F]" data-testid="current-phase-label">{cur?.name || "Start"}</span>
        <span className="font-mono text-slate-400 text-xs" data-testid="playback-time">{pb.t.toFixed(1)}s / {pb.duration.toFixed(1)}s</span>
      </div>
      <div className="relative pt-1">
        <input type="range" min={0} max={pb.duration} step={0.01} value={pb.t} onChange={(e) => pb.seek(parseFloat(e.target.value))} className="tpc-range w-full" aria-label="Timeline" data-testid="timeline-scrubber" />
        <div className="relative h-5">
          {phases.map((ph) => (
            <button key={ph.id} type="button" onClick={() => pb.seek(ph.time)} title={ph.name} className="absolute -translate-x-1/2 text-[10px] text-slate-400 hover:text-white whitespace-nowrap" style={{ left: `${(ph.time / pb.duration) * 100}%` }} data-testid={`phase-tick-${ph.id}`}>
              ▲{!compact && <span className="hidden sm:inline ml-0.5">{ph.name}</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn btn-ghost px-3" onClick={prev} aria-label="Previous phase" data-testid="btn-prev-phase"><SkipBack size={18} /></button>
        {pb.playing ? (
          <button className="btn btn-primary min-w-[96px]" onClick={pb.pause} data-testid="btn-play-pause-animation"><Pause size={18} /> Pause</button>
        ) : (
          <button className="btn btn-primary min-w-[96px]" onClick={pb.play} data-testid="btn-play-pause-animation"><Play size={18} /> Play</button>
        )}
        <button className="btn btn-ghost px-3" onClick={next} aria-label="Next phase" data-testid="btn-next-phase"><SkipForward size={18} /></button>
        <button className="btn btn-ghost px-3" onClick={pb.restart} aria-label="Restart" data-testid="btn-restart-animation"><RotateCcw size={18} /><span className="hidden sm:inline">Restart</span></button>
        <div className="flex rounded-xl border border-[#1F3358] overflow-hidden" role="group" aria-label="Speed" data-testid="select-speed-rate">
          {SPEEDS.map((s) => (
            <button key={s} onClick={() => pb.setSpeed(s)} className={`min-h-[44px] px-3 text-sm font-bold ${pb.speed === s ? "bg-[#FF6B00] text-white" : "text-slate-300 hover:bg-[#1A2A4A]"}`} data-testid={`speed-${s}`}>{s}×</button>
          ))}
        </div>
        {setTrails && (
          <button className={`btn px-3 ${trails ? "btn-active" : "btn-ghost"}`} onClick={() => setTrails(!trails)} aria-pressed={trails} data-testid="toggle-route-trails"><Route size={18} /><span className="hidden sm:inline">Routes</span></button>
        )}
        {setFull && (
          <button className="btn btn-ghost px-3 ml-auto" onClick={() => setFull(!full)} aria-label="Full screen" data-testid="btn-fullscreen">{full ? <Minimize2 size={18} /> : <Maximize2 size={18} />}</button>
        )}
      </div>
    </div>
  );
}
